import React, { useState, useEffect, useCallback, memo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { sets } from '../utils/api';
import SetGallery from '../components/SetGallery';
import { motion, AnimatePresence } from 'framer-motion';
import ShareSetModal from '../components/ShareSetModal';
import EditSetModal from '../components/EditSetModal';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';
import { toast } from 'react-toastify';

const MemoizedSetGallery = memo(SetGallery);

// Wrapper component to handle modal animations and isolation
const EditSetModalWrapper = memo(({ set, onClose, onSortChange, sortPreferences, onUpdate, onDelete }) => {
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key="edit-modal"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <EditSetModal
          set={set}
          onClose={onClose}
          onSortChange={onSortChange}
          sortPreferences={sortPreferences}
          onUpdate={onUpdate}
          onDelete={onDelete}
        />
      </motion.div>
    </AnimatePresence>
  );
});

const SetDetailsPage = () => {
  const { setId } = useParams();
  const navigate = useNavigate();
  const [set, setSet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [currentView, setCurrentView] = useState('grid');
  
  const [sortPreferences, setSortPreferences] = useState({
    grid: { field: 'purchase_date', direction: 'desc' },
    carousel: { field: 'purchase_date', direction: 'desc' },
    timeline: { field: 'purchase_date', direction: 'desc' },
    details: { field: 'year', direction: 'asc' }
  });

  // Memoized handlers
  const handleTagsChanged = useCallback(() => {
    setRefreshTrigger(prev => prev + 1);
  }, []);

  const handleSetDeleted = useCallback(async () => {
    try {
      await sets.delete(setId);
      toast.success('Set deleted successfully');
      navigate('/sets');
    } catch (err) {
      toast.error(err.message || 'Failed to delete set');
    }
    setIsDeleteModalOpen(false);
  }, [setId, navigate]);

  const handleViewModeChange = useCallback((mode) => {
    setCurrentView(mode);
  }, []);

  const handleCloseEditModal = useCallback(() => {
    setShowEditModal(false);
  }, []);

  const handleEditModalUpdate = useCallback(async () => {
    try {
      const updatedSet = await sets.get(setId);
      setSet(updatedSet);
      setRefreshTrigger(prev => prev + 1);
    } catch (err) {
      console.error('Failed to fetch updated set:', err);
    }
  }, [setId]);

  const handleSortChange = useCallback(async (newPreferences) => {
    if (!set) return;

    try {
      // Update local state first
      setSortPreferences(newPreferences);

      // Update the backend
      await sets.updateSortPreferences(set.id, newPreferences);

      // Update local state without fetching
      setSet(prev => ({
        ...prev,
        sort_preferences: newPreferences
      }));
    } catch (err) {
      console.error('Failed to update sort preferences:', err);
      // Revert local state on error
      setSortPreferences(prev => prev);
    }
  }, [set]);

  // Update sort preferences when set changes
  useEffect(() => {
    if (set?.sort_preferences) {
      setSortPreferences(prev => ({
        ...prev,
        ...set.sort_preferences
      }));
    }
  }, [set]);

  // Fetch set data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const fetchedSet = await sets.get(parseInt(setId));
        setSet(fetchedSet);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [setId]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 text-red-600 p-4 rounded-md">
          Error: {error}
        </div>
      </div>
    );
  }

  if (!set) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-yellow-50 text-yellow-600 p-4 rounded-md">
          Set not found
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/sets')}
              className="inline-flex items-center text-gray-600 hover:text-gray-900"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              <span className="ml-2">Back to Sets</span>
            </button>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowEditModal(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Settings
            </button>
            <button
              onClick={() => setShowShareModal(true)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
              </svg>
              {set.share_id ? 'Manage Share Link' : 'Share Set'}
            </button>
          </div>
        </div>

        {/* Set Gallery */}
        <AnimatePresence mode="wait">
          <motion.div
            key={`gallery-${set.id}-${refreshTrigger}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <MemoizedSetGallery 
              setId={set.id}
              onTagsChanged={handleTagsChanged}
              onSetDeleted={handleSetDeleted}
              initialViewMode={currentView}
              onViewModeChange={handleViewModeChange}
              sortPreferences={sortPreferences}
            />
          </motion.div>
        </AnimatePresence>

        {/* Share Modal */}
        <ShareSetModal
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          setId={set.id}
        />

        {/* Edit Set Modal */}
        {showEditModal && (
          <EditSetModalWrapper
            set={set}
            onClose={handleCloseEditModal}
            onSortChange={handleSortChange}
            sortPreferences={sortPreferences}
            onUpdate={handleEditModalUpdate}
            onDelete={() => setIsDeleteModalOpen(true)}
          />
        )}

        <DeleteConfirmationModal
          isOpen={isDeleteModalOpen}
          title="Delete Set"
          message={`Are you sure you want to delete "${set?.name}"? This action cannot be undone.`}
          onConfirm={handleSetDeleted}
          onCancel={() => setIsDeleteModalOpen(false)}
        />
      </div>
    </div>
  );
};

export default SetDetailsPage;
